/**
 * Renders an `encodeQr()` matrix to a self-contained SVG string — no DOM,
 * no canvas, no external asset. Row runs of adjacent dark modules are
 * merged into a single `<rect>` (fewer nodes, byte-identical result to one
 * `<rect>` per module) rather than emitting a path, since the module grid
 * is already an axis-aligned, uniformly-sized tiling that rects express
 * exactly.
 */
import { encodeQr } from './encode'
import { isPaint } from './paint'
import type { QrToSvgOptions } from './types'

/** Eight times the spec's quiet zone: past it the code is a speck in its own box. */
const WIDEST_MARGIN = 32

/** Merges a row's adjacent `true` runs into `[startCol, runLength]` pairs. */
function rowRuns(row: readonly boolean[]): [start: number, length: number][] {
	const runs: [number, number][] = []
	let runStart = -1
	for (let col = 0; col <= row.length; col++) {
		const dark = col < row.length && row[col]
		if (dark && runStart === -1) {
			runStart = col
		} else if (!dark && runStart !== -1) {
			runs.push([runStart, col - runStart])
			runStart = -1
		}
	}
	return runs
}

/**
 * Encodes `value` and renders it as a crisp, deterministic SVG string — the
 * one encoder both consumption shapes (this function and `<schmancy-qr>`)
 * share. Safe to call in SSR/Node: `encodeQr` and this renderer touch
 * nothing but strings and arrays. A bad option takes that option's default,
 * so nothing a caller binds here can reach the markup but a colour and a count.
 */
export function qrToSvg(value: string, options?: QrToSvgOptions | null): string {
	const { errorCorrectionLevel, margin: requestedMargin, moduleColor: requestedInk, backgroundColor: requestedGround } = options ?? {}
	const margin =
		typeof requestedMargin === 'number' && Number.isFinite(requestedMargin)
			? Math.min(Math.max(0, Math.trunc(requestedMargin)), WIDEST_MARGIN)
			: 4
	const moduleColor = isPaint(requestedInk) ? requestedInk : 'currentColor'
	const backgroundColor = isPaint(requestedGround) ? requestedGround : 'none'
	const { size, modules } = encodeQr(value, errorCorrectionLevel)
	const dimension = size + margin * 2

	const rects: string[] = []
	for (let row = 0; row < size; row++) {
		for (const [start, length] of rowRuns(modules[row])) {
			rects.push(`<rect x="${start + margin}" y="${row + margin}" width="${length}" height="1"/>`)
		}
	}

	const background =
		backgroundColor === 'none'
			? ''
			: `<rect x="0" y="0" width="${dimension}" height="${dimension}" fill="${backgroundColor}"/>`

	return (
		`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dimension} ${dimension}" shape-rendering="crispEdges">` +
		background +
		`<g fill="${moduleColor}">${rects.join('')}</g>` +
		`</svg>`
	)
}
